import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useImmerReducer } from 'use-immer'
import EnrollmentForm from '../../../components/EnrollmentForms/EnrollmentForm'
import Layout from '../../../components/Layout/Layout'
import { withRegistration } from '@srca-b2b/kp5-ui-common/utils/auth'
import { getMessageFromErrorCode } from '@srca-b2b/kp5-ui-common/utils/errorCodes'
import {
  knoxRegistrationSolutionName,
  knoxLoginUrls,
  isValidKpRedirectUrl
} from '@srca-b2b/kp5-ui-common/utils/helpers'
import { registrationLangCookieToPathPrefix } from '@srca-b2b/kp5-ui-common/utils/enrollmentHelpers'
import { getAmsLoginType } from '@srca-b2b/kp5-ui-common/utils/authAPIs'
import {
  submitRegistration,
  registerWithEmail,
  getRegisterLegalByCountry
} from '@srca-b2b/kp5-ui-common/utils/enrollmentAPIs'
import EnrollmentContextWrapper from '../../../context/enrollmentContext'
import { getEnrollmentInitialState, enrollmentReducer } from '../../../reducer/enrollmentReducer'
import { toast } from 'react-toastify'
import Cookies from 'js-cookie'
import cookie from 'next-cookies'
import Link from 'next/link'
import getConfig from 'next/config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
const { publicRuntimeConfig } = getConfig()

import styles from '../register.module.scss'
import KP5CONSTANTS from '@srca-b2b/kp5-ui-common/utils/constants'

import { useConfirmLeave } from '@srca-b2b/kp5-ui-common/hooks/modals/useConfirmLeaveModal'
import PlainHeader from '../../../components/PlainHeader/PlainHeader'
import { useSpinnerContext } from '@srca-b2b/kp5-ui-common/context/SpinnerContext'

const varnish_urls = knoxLoginUrls()

/**
 * Product Registration Form
 * - params can be set as prop from server or client (tests)
 * @param {function} t function pumped in by useTranslation for translation
 * @param {object} registrationData data from defaultValues api and will include which product and corresponding values we are dealing with via withRegistration
 * @param {number} step
 */
const Registration = ({
  productParam,
  registrationData,
  step,
  isSubAdminPath,
  errorCode,
  returnUrl,
  langPrefix,
  registrationReason,
  ...rest
}) => {
  const router = useRouter()
  const { setIsLoading } = useSpinnerContext()
  const { t } = useTranslation('common')
  const productTitle = knoxRegistrationSolutionName(productParam)
  const [state, dispatch] = useImmerReducer(
    enrollmentReducer,
    getEnrollmentInitialState(productParam, registrationData, step, productTitle)
  )
  const [excludedRoutes, setExcludedRoutes] = useState([])

  // this hide certain product params on the registration path url, but will still be able to send the param through the api for backend requirements.
  // we remove this because of requirement #1 of KP5-1027(related ticket KP5-1361) and also a solution for KP5-1519
  const hideRegPathProductParam = (productParam) => {
    const hiddenProductParam = [
      KP5CONSTANTS.REGISTER_PRODUCTS.SUITE,
      KP5CONSTANTS.REGISTER_PRODUCTS.PLATFORM_FOR_ENTERPRISE,
      KP5CONSTANTS.REGISTER_PRODUCTS.MOBILE_ENROLLMENT,
      KP5CONSTANTS.REGISTER_PRODUCTS.MANAGE,
      KP5CONSTANTS.REGISTER_PRODUCTS.EFOTA_ONE,
      KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE,
      KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE_ALIAS,
      KP5CONSTANTS.REGISTER_PRODUCTS.CONFIGURATOR,
      KP5CONSTANTS.REGISTER_PRODUCTS.CAPTURE
    ]

    if (hiddenProductParam.includes(productParam)) {
      router.replace(`/${router.locale}/register`, undefined, { shallow: true })
      // In general for all cases `router.locale === langPrefix
    } else if (productParam) {
      router.replace(`/${router.locale}/register/${productParam}`, undefined, { shallow: true })
    }

    if (isSubAdminPath && !productParam) {
      router.replace(`/${router.locale}/register`, undefined, { shallow: true })
    }
  }

  useEffect(() => {
    hideRegPathProductParam(productParam)

    if (errorCode) {
      // sign out if we get invalid AMS session
      if (errorCode === 'AMS_INVALID_USER_SESSION') {
        toast.error(getMessageFromErrorCode(errorCode))
        router.push(`${router.locale}/sso/logoutFinish`)
      }
    }
  }, [])

  const registerPath = productParam
    ? `/${router.locale}/register/${productParam}`
    : `/${router.locale}/register`
  const { ConfirmLeaveModal } = useConfirmLeave({
    shouldWarn: state.registrationData.email && !state.isSubmitting,
    desiredBackRoute: `/${router.locale}/sso/logoutFinish`,
    desiredNextRoute: registerPath,
    currentRoute: registerPath,
    excludedRoutes
  })

  useEffect(async () => {
    // watch for state changes and perform business logic accordingly
    if (state.isRegistrationStarted) {
      registerStart(state.registrationData.email, state.registrationData.locale)
    }
    if (state.isSubmitting) {
      processRegistration()
    }
    if (state.changeCountryStart) {
      loadLegalByCountry()
    }
    if (state.currentStep === 4) {
      setIsLoading(state.changeCountryStart)
    }
    if (state.isRegistrationInvalidDomain) {
      toast.error(t('Invalid_domain_message'))
    }
    if (state.isRegistrationInvalidEmailLength) {
      toast.error(t('Invalid_email_length_message'))
    }
  }, [
    state.isRegistrationStarted,
    state.isSubmitting,
    state.changeCountryStart,
    state.currentStep,
    state.isRegistrationInvalidDomain,
    state.isRegistrationInvalidEmailLength
  ])

  const loadLegalByCountry = async () => {
    const response = await getRegisterLegalByCountry(state.registrationData.country, productParam)
    if (response.data) {
      dispatch({
        type: 'CHANGED_COMPANY_COUNTRY_COMPLETE',
        ...response.data
      })
    }
  }

  const registerStart = async (email, locale) => {
    const loginTypeResponse = await getAmsLoginType(email)
    if (loginTypeResponse.responseJSON === 'AZURE_AD') {
      const redirectUrl = `/en/federated-id/login?email=${encodeURIComponent(email)}`
      setExcludedRoutes([redirectUrl])
      router.push(redirectUrl)
      return null
    }
    const response = await registerWithEmail(email)
    if (response.error === 'REGISTRATION_INVALID_EMAIL_DOMAIN') {
      dispatch({ type: 'INVALID_EMAIL_DOMAIN' })
    } else if (response.error === 'REGISTRATION_INVALID_EMAIL_LENGTH') {
      dispatch({ type: 'INVALID_EMAIL_LENGTH' })
    } else if (response.data) {
      if (productParam) {
        Cookies.set('registrationProduct', productParam)
      }

      if (registrationReason) {
        Cookies.set('registrationReason', registrationReason)
      }
      Cookies.set('registrationLang', locale)

      let redirectUrl = `/${locale}/sso/signup?email=${encodeURIComponent(
        email
      )}&locale=${encodeURIComponent(locale)}`

      if (response.data.saExists === true) {
        redirectUrl = `/${locale}/login/samsung?email=${encodeURIComponent(
          email
        )}&locale=${encodeURIComponent(locale)}`
      }
      // Support remote backend during development.
      if (publicRuntimeConfig.remoteBackendBaseUrl) {
        redirectUrl = `${publicRuntimeConfig.remoteBackendBaseUrl}${redirectUrl}`
      }
      setExcludedRoutes([redirectUrl])
      console.log(`registerStart going to ${redirectUrl}`)
      router.push(redirectUrl)
      return null
    } else {
      dispatch({ type: 'STARTED_REGISTRATION_FAILED' })
    }
  }

  const processRegistration = async () => {
    setIsLoading(true)
    const savedRegistrationLang = registrationLangCookieToPathPrefix(
      Cookies.get('registrationLang')
    )
    const userRegistrationLang = savedRegistrationLang ? savedRegistrationLang : router.locale
    const response = await submitRegistration(
      state.registrationData,
      productParam,
      null,
      userRegistrationLang,
      registrationReason
    )
    setIsLoading(false)
    if (response.statusCode === 200) {
      dispatch({ type: 'SUBMIT_REGISTRATION_COMPLETE' })
      // TODO: Show success toast
      if (returnUrl) {
        router.push(returnUrl)
        return null
      }
      router.push(`/${userRegistrationLang}/dashboard`)
    } else {
      dispatch({ type: 'SUBMIT_REGISTRATION_ERROR', errorMessage: response.message })
      // TODO: Show proper error toast
      toast.error(
        <>
          <p>{response.message}</p>
          <span className="rqID" hidden={!response?.requestID}>
            Request ID: {response?.requestID}
          </span>
        </>
      )
    }
    Cookies.remove('registrationProduct')
    Cookies.remove('registrationLang')
    Cookies.remove('registrationReason')
  }

  const IntroText = () => {
    switch (productParam) {
      case 'kg':
      case 'kdp':
      case 'msp':
      case 'kscp':
      default:
        return (
          <>
            {t('Enrollment_Intro_Sentence_1')}
            <br />
            {t('Enrollment_Intro_Sentence_2')}&nbsp;
            <Link href={varnish_urls.partners.forResellers}>
              <a>{t('Knox_Deployment_Program')}</a>
            </Link>
            .<br />
            {t('Enrollment_Intro_Sentence_3')}&nbsp;
            <Link href={varnish_urls.partners.forMSPs}>
              <a>{t('Knox_MSP_Program')}</a>
            </Link>
            .
          </>
        )
    }
  }

  const headerTitle = t('Register_for')
  const samsungKnoxTitle = t('Samsung_Knox')

  return (
    <EnrollmentContextWrapper state={state} dispatch={dispatch}>
      <Layout
        className={styles.registerLayout}
        containerClassName={styles.registerLayoutContainer}
        title={`${headerTitle} ${productTitle} | ${samsungKnoxTitle}`}
        productParam={productParam}
        withFooter
      >
        <PlainHeader altLogo={t('Samsung_Knox')} />
        <EnrollmentForm
          productParam={productParam}
          isSubAdminPath={isSubAdminPath}
          registrationReason={registrationReason}
        >
          <IntroText />
        </EnrollmentForm>
        <ConfirmLeaveModal />
      </Layout>
    </EnrollmentContextWrapper>
  )
}

export const getServerSideProps = withRegistration(
  async ({
    context,
    productParam,
    registrationData,
    step,
    errorCode,
    langPrefix,
    registrationReason
  }) => {
    const language = langPrefix || context.locale
    const props = {
      ...(await serverSideTranslations(language, ['common']))
    }
    props.productParam = productParam ?? null
    if (!props.productParam) {
      const productParamFromCookie = Cookies.get('registrationProduct')
      props.productParam = productParamFromCookie ?? null
    }
    props.isSubAdminPath = context.query.isSubAdminPath ? true : false
    if (props.isSubAdminPath) {
      // Prevent the B2C sub-admin register page from being indexed.
      context.res.setHeader('X-Robots-Tag', 'noindex')
    }
    props.langPrefix = langPrefix

    props.registrationReason = registrationReason
    if (!props.registrationReason) {
      const registrationReasonFromCookie = Cookies.get('registrationReason')
      props.registrationReason = registrationReasonFromCookie ?? null
    }

    if (registrationData) {
      props.registrationData = registrationData
      props.loggedInUser = registrationData
    }
    if (step) {
      props.step = step
    }
    if (errorCode) {
      props.errorCode = errorCode
    }

    const returnUrl = cookie(context).returnUrl

    // Redirect to returnUrl if valid.
    if (isValidKpRedirectUrl(returnUrl)) {
      props.returnUrl = returnUrl
    } else if (returnUrl) {
      props.returnUrl = '/en/404'
    }
    return { props }
  }
)

export default Registration
